import { CButton } from "components/UI/Button/Buttons";
import { GInput } from "components/UI/Inputs/GInput";
import { ModalWrapper } from "components/UI/wrapper/modal-wrapper";
import zoomIcon from "assets/svg/zoomIcon.svg";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import DateTimeRangeCard from "components/UI/date/event-date";
import { Switch } from "antd";
import instance from "lib/axiosInterceptor";
import {
  AppType,
  ConnectedAppsInterface,
  EventLinkInterface,
} from "interfaces/interfaces-data";
import teamsIcon from "assets/svg/teams-icon.svg";
import zoomAppIcon from "assets/svg/zoom-bg.svg";
import meetIcon from "assets/svg/meet-bg.svg";
import radioButtonUnfilled from "assets/svg/radio-button-unfilled.svg";
import radioButtonFilled from "assets/svg/radio-button-filled.svg";

export const setGmpPlaceholder = (el: any, text: string) => {
  const sr: ShadowRoot | null | undefined = el?.shadowRoot;
  if (!sr) return;

  // the internal input lives inside shadow dom
  const input =
    (sr.querySelector("input") as HTMLInputElement | null) ??
    (sr.querySelector('[role="combobox"]') as HTMLInputElement | null);

  if (!input) return;

  input.setAttribute("placeholder", text);
};

export const injectNoFocusRing = (el: any) => {
  const sr: ShadowRoot | null | undefined = el?.shadowRoot;
  if (!sr) return; // case B requires open shadow root

  // prevent duplicates
  if (sr.querySelector('style[data-no-focus-ring="true"]')) return;

  const style = document.createElement("style");
  style.setAttribute("data-no-focus-ring", "true");
  style.textContent = `
    /* kill google focus ring */
    
     button[aria-label="Clear"],
    .clear-button,
    .input-clear,
    .end-slot,
    [part="clear-button"] {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }


    .focus-ring {
      display: none !important;
      opacity: 0 !important;
      box-shadow: none !important;
      outline: none !important;
      border: none !important;
    }

    /* extra safety */
    .input-container:focus-within {
      outline: none !important;
      box-shadow: none !important;
    }

    input:focus,
    input:focus-visible {
      outline: none !important;
      box-shadow: none !important;
    }
  `;

  sr.appendChild(style);
};
export const patchGmpPlaceAutocompleteShadowOnce = () => {
  const w = window as any;
  if (w.__gmp_shadow_patch_done) return;
  w.__gmp_shadow_patch_done = true;

  const original = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function (init: ShadowRootInit) {
    if (this.localName === "gmp-place-autocomplete") {
      return original.call(this, { ...init, mode: "open" });
    }
    return original.call(this, init);
  };
};

const getComponentText = (components: any[] | undefined, types: string[]) => {
  if (!components?.length) return "";
  const match = components.find((c) =>
    types.some((type) => c.types?.includes?.(type)),
  );
  return match?.longText || match?.shortText || "";
};

export const buildFriendlyAddress = (place: any) => {
  const components = place.addressComponents as any[] | undefined;

  // Prefer a human name first (e.g., Ojoo Bus Terminal)
  const name = place.displayName || "";

  // Prefer longText to avoid abbreviations like "Expy"
  const route = getComponentText(components, ["route"]);
  const locality = getComponentText(components, ["sublocality", "locality"]);
  const country = getComponentText(components, ["country"]);

  const pieces = [name, route, locality, country].filter(Boolean);

  // If everything is missing, fall back to formattedAddress
  if (!pieces.length && place.formattedAddress) return place.formattedAddress;

  return pieces.join(", ");
};



export const EventLocationModal = ({
  showModal,
  setShowModal,
  location,
  setLocation,
  setGoogleCoordinates,
  extra,
  setPlaceId,
  hideMain,
  eventLink,
  setEventLink,
  setTba,
  tba,
}: {
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  location: string;
  setLocation: (val: string) => void;
  setGoogleCoordinates: (val?: { lat: number; long: number }) => void;
  extra?: boolean;
  setPlaceId?: (val: string) => void;
  hideMain?: boolean;
  eventLink?: EventLinkInterface | null;
  setEventLink: Dispatch<SetStateAction<EventLinkInterface | null>>;
  setTba: (val: boolean) => void;
  tba: boolean;
}) => {
  const [activeState, setActiveState] = useState(0);
  const inputWrapperRef = useRef<HTMLDivElement | null>(null);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [apps, setApps] = useState<ConnectedAppsInterface[]>([]);
  const [appActiveIndex, setAppActiveIndex] = useState(-1);
  const [customAppActive, setCustomAppActive] = useState<
    "zoom" | "meet" | "teams" | null
  >(null);

  const getApps = async () => {
    try {
      const result = await instance.get(`/Apps`);
      setApps(result.data);
    } catch (e) {}
  };

  const defaultApps = [
    {
      name: "Teams",
      slug: "teams",
      action: () => {
        setCustomAppActive("teams");
      },
      icon: teamsIcon,
    },
    {
      name: "Zoom",
      slug: "zoom",
      action: () => {
        setCustomAppActive("zoom");
      },
      icon: zoomAppIcon,
    },
    {
      name: "Meet",
      slug: "meet",
      action: () => {
        setCustomAppActive("meet");
      },
      icon: meetIcon,
    },
  ];



  useEffect(() => {
    if (!showModal || activeState !== 0) return;

    let cancelled = false;

    const loaderRef = (EventLocationModal as any).__gm_loader_ref || {
      current: null as Loader | null,
    };

    if (!loaderRef.current) {
      loaderRef.current = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAP_API as string,
        version: "beta", // PlaceAutocompleteElement may require beta in some setups :contentReference[oaicite:3]{index=3}
        libraries: ["places"],
      });
      (EventLocationModal as any).__gm_loader_ref = loaderRef;
    }

    let placeAutocompleteEl: any = null;
    let onSelect: any = null;

    const init = async () => {
      try {
        await loaderRef.current!.load();
        if (cancelled) return;

        const wrapper = inputWrapperRef.current;
        if (!wrapper || !window.google?.maps) return;

        // Import the NEW Places library
        const { PlaceAutocompleteElement } = (await google.maps.importLibrary(
          "places",
        )) as any;

        // Clear any previous mount
        wrapper.querySelector('[data-gmp="place-autocomplete"]')?.remove();
        patchGmpPlaceAutocompleteShadowOnce();
        // Create + mount the new element
        placeAutocompleteEl = new PlaceAutocompleteElement();
        placeAutocompleteEl.setAttribute("data-gmp", "place-autocomplete");

        // Optional: restrict/bias results
        // placeAutocompleteEl.includedRegionCodes = ["ng"]; // Nigeria
        // placeAutocompleteEl.locationBias = { radius: 50000, center: { lat: 6.5244, lng: 3.3792 } }; // Lagos

        wrapper.appendChild(placeAutocompleteEl);
        requestAnimationFrame(() => {
          setGmpPlaceholder(placeAutocompleteEl, "Enter your address here");
          injectNoFocusRing(placeAutocompleteEl);
        });

        onSelect = async (e: any) => {
          if (cancelled) return;

          // Docs show event payload provides a prediction; convert to Place + fetch fields :contentReference[oaicite:4]{index=4}
          const placePrediction = e?.placePrediction;
          if (!placePrediction) return;

          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: [
              "id",
              "displayName",
              "formattedAddress",
              "location",
              "addressComponents",
              // "plusCode",
            ],
          });

          const placeId = place.id || "";
          if (setPlaceId) setPlaceId(placeId);

          const address = buildFriendlyAddress(place);

          // update your UI state
          setLocation(address);

          const lat = place.location?.lat?.();
          const lng = place.location?.lng?.();

          if (lat != null && lng != null) {
            setGoogleCoordinates({ lat, long: lng });
          }
        };

        // Event name in docs: gmp-select :contentReference[oaicite:5]{index=5}
        placeAutocompleteEl.addEventListener("gmp-select", onSelect);
      } catch {
        // no-op
      }
    };

    const raf = requestAnimationFrame(init);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);

      if (placeAutocompleteEl && onSelect) {
        placeAutocompleteEl.removeEventListener("gmp-select", onSelect);
      }
      placeAutocompleteEl?.remove();
      placeAutocompleteEl = null;
      onSelect = null;
    };
  }, [showModal, activeState]);

  useEffect(() => {
    getApps();
  }, []);
  return (
    <ModalWrapper
      open={showModal}
      onCancel={() => {
        setShowModal(false);
      }}
      noHeader
    >
      {extra && (
        <>
          <div className="bg-[#F2F2F2] p-3 rounded-lg">
            <p className="text-black text-lg font-sfMedium">Manage Schedule</p>
          </div>

          <div className="py-2 ">
            <p className="text-[#344054] mb-1 font-sfLight">Start Time</p>
            <DateTimeRangeCard hideAllLabel={extra} />
          </div>
        </>
      )}
      {!hideMain && (
        <>
          <div className="bg-[#F2F2F2]  p-4 rounded-lg">
            <p className="text-xl text-[#000000] font-monaMedium">
              Add a location or virtual link
            </p>
            <p className="mt-1 text-[#474747] text-sm font-monaRegular">
              Specify if your event is a physical or virtual event
            </p>
          </div>
          <div>
            <div className="flex justify-between my-2">
              <p className="text-[#3D4045] font-sfRegular text-base">
                Set as TBA
              </p>
              <Switch
                checked={tba}
                onChange={(val) => {
                  setTba(val);
                }}
              />
            </div>
            <div className="flex items-center mb-3">
              <button
                onClick={() => setActiveState(0)}
                className={`flex items-center justify-center py-3 w-full ${
                  activeState === 0
                    ? "border-b-2 border-[#FF6929]"
                    : "border-b-2 border-[#F3F0EB]"
                }`}
              >
                <p
                  className={`whitespace-nowrap text-base ${
                    activeState === 0
                      ? "text-[#FF6929] font-sfSemibold"
                      : "text-[#666563] font-sfRegular"
                  }`}
                >
                  Physical Address
                </p>
              </button>
              <button
                onClick={() => setActiveState(1)}
                className={`flex items-center justify-center py-3 w-full ${
                  activeState === 1
                    ? "border-b-2 border-[#FF6929]"
                    : "border-b-2 border-[#F3F0EB]"
                }`}
              >
                <p
                  className={`whitespace-nowrap text-base ${
                    activeState === 1
                      ? "text-[#FF6929] font-sfSemibold"
                      : "text-[#666563] font-sfRegular"
                  }`}
                >
                  Virtual
                </p>
              </button>
            </div>
          </div>

          {activeState === 0 && (
            <>
              {/* <div ref={inputWrapperRef} className="mt-2">
                <GInput
                  inputValue={location}
                  setInput={setLocation}
                  label="Location"
                  placeholder="Enter your address or virtual link here"
                />
              </div> */}
              <div ref={inputWrapperRef} />

              <div className="mt-2">
                <CButton
                  text="Add location"
                  action={() => setShowModal(false)}
                  fullWidth
                  variant="dark"
                />
              </div>
            </>
          )}
          {activeState === 1 && (
            <div className="mt-2">
              {apps.length ? (
                <>
                  {apps.map((item, index) => {
                    return (
                      <button
                        onClick={() => {
                          setAppActiveIndex((prev) =>
                            prev === index ? -1 : index,
                          );
                        }}
                        className={`border ${
                          appActiveIndex === index
                            ? "border-[#FF6929] bg-[#FFF0EA]"
                            : "border-[#EAECF0] bg-[#FFFFFF]"
                        } p-2 flex items-center justify-between rounded-xl  w-full  mb-1`}
                        key={item.id}
                      >
                        <div className="flex items-center gap-2">
                          <img
                            src={
                              item.app === AppType.Microsoft_Teams
                                ? teamsIcon
                                : item.app === AppType.Google_Meet
                                  ? meetIcon
                                  : zoomAppIcon
                            }
                            alt=""
                          />
                          <p className="text-[#344054] font-sfMedium">
                            {item.app === AppType.Microsoft_Teams
                              ? "Microsoft Teams"
                              : item.app === AppType.Google_Meet
                                ? "Google Meet"
                                : "Zoom"}
                          </p>
                        </div>
                        <img
                          src={
                            appActiveIndex === index
                              ? radioButtonFilled
                              : radioButtonUnfilled
                          }
                          alt=""
                        />
                      </button>
                    );
                  })}
                  <div className="mt-2">
                    <CButton longer variant="dark" text="Save" fullWidth />
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-[#101828] text-base font-sfMedium my-3">
                    Which of the app is the meeting taking place?{" "}
                  </p>
                  <div className="flex items-center gap-2">
                    {defaultApps.map((item, index) => {
                      return (
                        <button
                          onClick={() => {
                            setEventLink((prev) =>
                              prev
                                ? { ...prev, app: item.name }
                                : { app: item.name, link: "" },
                            );
                            item.action();
                          }}
                          className={`border ${
                            customAppActive === item.slug ||
                            eventLink?.app.toLowerCase().includes(item.slug)
                              ? "border-[#FF6929] bg-[#FFF0EA]"
                              : "border-[#EAECF0] bg-[#FFFFFF]"
                          } p-2 flex items-center justify-between rounded-xl  w-full  mb-1`}
                        >
                          <div className="flex items-center gap-2">
                            <img src={item.icon} alt="" />
                            <p className="text-[#344054] font-sfMedium">
                              {item.name}
                            </p>
                          </div>
                          <img
                            src={
                              customAppActive === item.slug ||
                              eventLink?.app.toLowerCase().includes(item.slug)
                                ? radioButtonFilled
                                : radioButtonUnfilled
                            }
                            alt=""
                          />
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <GInput
                      value={eventLink?.link ?? ""}
                      label="Meeting link"
                      placeholder="Paste the meeting link here"
                      setInput={(e) => {
                        setEventLink((prev) =>
                          prev ? { ...prev, link: e } : { app: "", link: e },
                        );
                      }}
                    />
                  </div>
                  <CButton
                    action={() => {
                      setShowModal(false);
                    }}
                    text="Save"
                    variant="dark"
                    longer
                    fullWidth
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </ModalWrapper>
  );
};
